import React, { useState } from "react";
import { Text } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { WorkflowComment } from "../../types";
import { CommentCard } from "./CommentCard";
import * as styles from "./styles_vanilla.css";
import { buildThreads, getInitials } from "./utils";

type CommentsListProps = {
  comments: WorkflowComment[];
  isGPX?: boolean;
};

type ThreadDividerProps = {
  replies: WorkflowComment[];
  expanded: boolean;
  onToggle: () => void;
};

const ThreadDivider: React.FC<ThreadDividerProps> = ({ replies, expanded, onToggle }) => {
  const count = replies.length;
  const label = expanded
    ? `${count} ${count === 1 ? "reply" : "replies"}`
    : `${count} ${count === 1 ? "reply" : "replies"}`;
  const firstReplier = replies[0].author;

  return (
    <button
      type="button"
      className={styles.threadDivider}
      onClick={onToggle}
      aria-expanded={expanded}
      aria-label={`${expanded ? "Collapse" : "Expand"} ${label}`}
    >
      <div className={styles.dividerLineLeft} />
      <div className={styles.dividerAvatarWrap}>
        <Text
          variant="secondary-sm"
          color={Colors.Neutral700}
          className={styles.dividerAvatarText}
        >
          {getInitials(firstReplier.name)}
        </Text>
      </div>
      <Text variant="secondary-sm" color={Colors.Neutral600}>
        {label}
      </Text>
      <div className={styles.dividerLineRight} />
    </button>
  );
};

export const CommentsList: React.FC<CommentsListProps> = ({ comments, isGPX = false }) => {
  const threads = buildThreads(comments);
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(threads.map((t) => t.parent.id)),
  );

  const toggleThread = (parentId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(parentId)) {
        next.delete(parentId);
      } else {
        next.add(parentId);
      }
      return next;
    });
  };

  return (
    <div className={styles.threadList} data-testid="workflow-comments-list">
      {threads.map(({ parent, replies }) => {
        const isExpanded = expanded.has(parent.id);
        return (
          <div key={parent.id}>
            <CommentCard comment={parent} isGPX={isGPX} />
            {replies.length > 0 && (
              <>
                <ThreadDivider
                  replies={replies}
                  expanded={isExpanded}
                  onToggle={() => toggleThread(parent.id)}
                />
                {isExpanded && (
                  <div
                    className={styles.replyList}
                    data-testid={`workflow-comment-${parent.id}-replies`}
                  >
                    {replies.map((reply) => (
                      <CommentCard key={reply.id} comment={reply} isGPX={isGPX} />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};
